<template>
  <section>
    <content-section :spacing="false">
      <div class="content-spacing">
        <div class="grid grid-cols-1 mb-10 md:grid-cols-2">
          <div>
            <title-plus
              :title="
                $t(
                  'components.orgUserDetailsManagement.diveIntoTab.inspectionRecords.headline'
                )
              "
              :hide-plus="true"
            />
          </div>

          <div class="flex md:justify-end">
            <date-range-picker
              @apply-date="onApplyFilterDateRange"
              @cancel-date="onApplyFilterDateRange"
            />
          </div>
        </div>
      </div>
      <FSTable
        :fst-id="fstId"
        :headers="getTableHeaders"
        :endpoint="getEndpoint"
        :qso="getEndpointOptions"
        :exportType="exportKey.INSPECTION_RECORDS"
        :exportFor="`vehicle-inspection-records`"
        :exportFromURL="true"
      >
        <template #topLeft="{ slotWidth }">
          <keep-alive>
            <FSTableFilter
              :fst-id="fstId"
              :slot-width="slotWidth"
              :options="filterOptions"
            />
          </keep-alive>
        </template>
        <template v-slot:default="{ data }">
          <template v-for="(item, itemIndex) in data">
            <template v-if="getTableMode === 'full'">
              <FSTableRow :key="itemIndex" text-fallback-always>
                <FSTableRowItem
                  :text="
                    `${
                      item.inspected_at
                        ? new Date(item.inspected_at).toISOString()
                        : '--'
                    }`
                  "
                  :date="true"
                />
                <FSTableRowItem :text="item.type_display_text" />

                <FSTableRowItem>
                  <router-link
                    v-if="item.inspector"
                    class="font-bold text-blue-600 capitalize cursor-pointer"
                    :to="{
                      name: 'ViewOrganizationUserProfile',
                      params: { id: item.inspector.id },
                    }"
                    target="_blank"
                  >
                    {{ item.inspector.full_name || '--' }}
                  </router-link>
                  <span v-else>--</span>
                </FSTableRowItem>
                <FSTableRowItem>
                  <router-link
                    class="font-bold text-blue-600 capitalize cursor-pointer"
                    :to="{
                      name: 'ViewVehicleProfile',
                      params: { id: item.inspection.vehicle.id },
                    }"
                    target="_blank"
                  >
                    {{ item.inspection.vehicle.qr_code || '--' }}
                  </router-link>
                </FSTableRowItem>

                <FSTableRowItem>
                  {{ item.faulty_part_names || '--' }}
                </FSTableRowItem>
                <FSTableRowItem>
                  <XStatus
                    :text="item.status_display_text"
                    :variant="getItemVariant(item.status)"
                    profile="marketplace"
                  />
                </FSTableRowItem>
              </FSTableRow>
            </template>
            <template v-if="getTableMode === 'responsive'">
              <FSTableRow
                :key="`fs-table-row-${itemIndex}`"
                text-fallback-always
              >
                <FSTableRowItem>
                  <div
                    class="col-span-1 focus:text-gray-400"
                    @click="toggle(itemIndex)"
                  >
                    <i
                      class="fas fa-minus-circle"
                      style="color:#d90a20;"
                      v-if="opened.includes(itemIndex)"
                    ></i>
                    <i class="fas fa-plus-circle" v-else></i>
                  </div>
                </FSTableRowItem>
                <FSTableRowItem :text="item.type_display_text" />
                <FSTableRowItem>
                  <router-link
                    v-if="item.inspector"
                    class="font-bold text-blue-600 capitalize cursor-pointer"
                    :to="{
                      name: 'ViewOrganizationUserProfile',
                      params: { id: item.inspector.id },
                    }"
                    target="_blank"
                  >
                    {{ item.inspector.full_name || '--' }}
                  </router-link>
                  <span v-else>--</span>
                </FSTableRowItem>
                <FSTableRowItem>
                  <XStatus
                    :text="item.status_display_text"
                    :variant="getItemVariant(item.status)"
                    profile="marketplace"
                  />
                </FSTableRowItem>
              </FSTableRow>
              <FSTableRow v-if="opened.includes(itemIndex)" :key="itemIndex">
                <td colspan="10">
                  <div
                    class="grid grid-cols-10 items-center w-full expand-item"
                  >
                    <div class="col-span-5 left-text">
                      {{
                        $t(
                          'components.orgUserDetailsManagement.diveIntoTab.inspectionRecords.table.columns.time'
                        )
                      }}
                    </div>
                    <div class="col-span-5 right-text">
                      {{
                        `${
                          item.inspected_at
                            ? getFormattedDateTime(
                                item.inspected_at,
                                'D MMM YYYY, h:mm:ss a'
                              )
                            : '--'
                        }`
                      }}
                    </div>
                  </div>
                  <div
                    class="grid grid-cols-10 items-center w-full expand-item"
                  >
                    <div class="col-span-5 left-text">
                      {{
                        $t(
                          'components.orgUserDetailsManagement.diveIntoTab.inspectionRecords.table.columns.issuesIdentified'
                        )
                      }}
                    </div>
                    <div class="col-span-5 right-text">
                      {{ item.faulty_part_names || '--' }}
                    </div>
                  </div>
                  <div
                    class="grid grid-cols-10 items-center w-full expand-item"
                  >
                    <div class="col-span-5 left-text">
                      {{
                        $t(
                          'components.orgUserDetailsManagement.diveIntoTab.inspectionRecords.table.columns.qrCode'
                        )
                      }}
                    </div>
                    <div class="col-span-5 right-text">
                      <router-link
                        class="font-bold text-blue-600 capitalize cursor-pointer"
                        :to="{
                          name: 'ViewVehicleProfile',
                          params: { id: item.inspection.vehicle.id },
                        }"
                        target="_blank"
                      >
                        {{ item.inspection.vehicle.qr_code || '--' }}
                      </router-link>
                    </div>
                  </div>
                </td>
              </FSTableRow>
            </template>
          </template>
        </template>
      </FSTable>
    </content-section>
  </section>
</template>

<script>
import {
  FSTable,
  FSTableRow,
  FSTableRowItem,
  FSTableFilter,
  onApplyFilterDateRange,
} from '@/components/fs-table'
import XStatus from '@/components/badge/XStatus'
import { useEndpoints } from '@/composables'
import { getFormattedDateTime } from '@/utils/datetime'
import { exportKey } from '@/utils/export-data/helper.js'
export default {
  name: 'ViewOrganizationUserInspectionRecords',
  props: {
    fstId: {
      type: String,
      required: false,
      default: 'operator-vehicle-inspection-records',
    },
  },

  components: {
    ContentSection: () => import('@/components/layout/ContentSection'),
    TitlePlus: () => import('@/components/ui/TitlePlus'),
    DateRangePicker: () =>
      import('@/components/picker/date-range/DateRangePicker'),
    FSTable,
    FSTableRow,
    FSTableRowItem,
    FSTableFilter,
    XStatus,
  },
  data() {
    return {
      exportKey,
      windowWidth: window.innerWidth,
      qso: { prepend: '&', append: '' },
      tableHeaders: [
        {
          text: this.$t(
            'components.orgUserDetailsManagement.diveIntoTab.inspectionRecords.table.columns.time'
          ),
          width: '20%',
          sort: null,
        },
        {
          text: this.$t(
            'components.orgUserDetailsManagement.diveIntoTab.inspectionRecords.table.columns.type'
          ),
          width: '15%',
          sort: null,
        },
        {
          text: this.$t(
            'components.orgUserDetailsManagement.diveIntoTab.inspectionRecords.table.columns.inspector'
          ),
          width: '15%',
          sort: null,
        },
        {
          text: this.$t(
            'components.orgUserDetailsManagement.diveIntoTab.inspectionRecords.table.columns.qrCode'
          ),
          width: '10%',
          sort: null,
        },
        {
          text: this.$t(
            'components.orgUserDetailsManagement.diveIntoTab.inspectionRecords.table.columns.issuesIdentified'
          ),
          width: '25%',
          sort: null,
        },
        {
          text: this.$t(
            'components.orgUserDetailsManagement.diveIntoTab.inspectionRecords.table.columns.status'
          ),
          width: '20%',
          sort: null,
        },
      ],
      resTableHeaders: [
        { text: '', width: '10%', sort: null },
        {
          text: this.$t(
            'components.orgUserDetailsManagement.diveIntoTab.inspectionRecords.table.columns.type'
          ),
          width: '20%',
          sort: null,
        },
        {
          text: this.$t(
            'components.orgUserDetailsManagement.diveIntoTab.inspectionRecords.table.columns.inspector'
          ),
          width: '20%',
          sort: null,
        },
        {
          text: this.$t(
            'components.orgUserDetailsManagement.diveIntoTab.inspectionRecords.table.columns.status'
          ),
          width: '20%',
          sort: null,
        },
      ],
      opened: [],
      filterOptions: [
        {
          key: 'status',
          type: 'checkbox',
          input: [
            { text: 'Pending', value: 0 },
            { text: 'In progress', value: 1 },
            // { text: 'Cancelled', value: 2 },
            { text: 'Failed', value: 3 },
            { text: 'Passed', value: 4 },
          ],
          title: 'Status',
        },
        {
          key: 'type',
          type: 'checkbox',
          input: [
            { text: 'Routine', value: 0 },
            {
              text: 'Re-inspection',
              value: 1,
            },
          ],
          title: 'Type',
        },
      ],
    }
  },

  computed: {
    getTableHeaders() {
      if (this.windowWidth <= 930) {
        return this.resTableHeaders
      } else {
        return this.tableHeaders
      }
    },
    getTableMode() {
      if (this.windowWidth <= 930) {
        return 'responsive'
      } else {
        return 'full'
      }
    },
    getEndpoint() {
      return useEndpoints.operationPrimer.inspectionAction.inspectionRecords()
    },
    getEndpointOptions() {
      return {
        append: `&inspector=${this.$route.params.id}`,
        prepend: `?`,
      }
    },
  },

  async created() {
    // this.filterOptions = await this.generateFilterOptions()
  },
  mounted() {
    this.$nextTick(() => {
      window.addEventListener('resize', this.onResize)
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    onApplyFilterDateRange,
    getFormattedDateTime,
    async generateFilterOptions() {
      let filters = [
        {
          key: 'status',
          type: 'checkbox',
          input: [
            { text: 'Pending', value: 0 },
            { text: 'In progress', value: 1 },
            // { text: 'Cancelled', value: 2 },
            { text: 'Failed', value: 3 },
            { text: 'Passed', value: 4 },
          ],
          title: 'Status',
        },
        {
          key: 'type',
          type: 'checkbox',
          input: [
            { text: 'Routine', value: 0 },
            {
              text: 'Re-inspection',
              value: 1,
            },
          ],
          title: 'Type',
        },
      ]

      // if (this.$acl.canView('Operators')) {
      //   let filterableOperators = []
      //   await this.$http
      //     .get('/dashboard/operators/?dropdown')
      //     .then(({ data }) => {
      //       if (data.data.length) {
      //         data.data.forEach((operator) => {
      //           filterableOperators.push({
      //             text: operator.full_name,
      //             value: operator.id,
      //           })
      //         })
      //       }
      //     })

      //   const owner = { text: this.$user.full_name, value: this.$user.id }

      //   filters.unshift({
      //     key: 'inspector',
      //     type: 'radio',
      //     input: [...filterableOperators, owner],
      //     title: 'Inspector',
      //   })
      //   this.filterOptions = filters
      // } else {
      //   this.filterOptions = filters
      // }

      this.filterOptions = filters
    },
    getItemVariant(data) {
      if (data === 0) return 'gray'
      if (data === 1) return 'blue'
      if (data === 2) return 'orange'
      if (data === 3) return 'red'
      if (data === 4) return 'green'
      return 'gray'
    },

    onResize() {
      this.windowWidth = window.innerWidth
    },

    toggle(id) {
      const index = this.opened.indexOf(id)
      if (index > -1) {
        this.opened.splice(index, 1)
      } else {
        this.opened.push(id)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.left-text {
  font-size: 14px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #3a4048;
}
.right-text {
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #6d6d75;
}
</style>
